import React, { useState, useMemo } from 'react';
import JurorCard from './JurorCard/JurorCard';
import { Paper } from '@mui/material';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import useStyles from './Case.styles';
import { getActiveDrag, getMode } from '../../state/ethereal/selectors';
import DismissQuadrant from "./DismissQuadrant";
import { useCase, useDismissedJurors } from '../../db/hooks';
import { updateJuror } from '../../db/actions';
import { useViewBasedCardWidth } from '../../util/hooks';
import {
  DemographicsFilterSelect,
  DismissalReasonChips,
  FilterBar,
  FilterSearchField,
  ScoreFilterControl,
  jurorMatchesDemographics,
  jurorMatchesReasons,
  jurorMatchesScoreFilter,
  jurorMatchesText,
  useDebouncedValue,
  useSharedJurorFilters,
} from './JurorFilterControls';

const DismissedArea = ({ caseId, civilCase, demographicCategories, showDemographics }) => {
  const activeDrag = useSelector(getActiveDrag);

  if (activeDrag) {
    return (
      <DismissQuadrant key="dismissed-quadrants"
                       civilCase={civilCase}
                       caseId={caseId} />
    );
  }

  return (
    <DismissedBox key="dismissed-box"
                  caseId={caseId}
                  civilCase={civilCase}
                  demographicCategories={demographicCategories}
                  showDemographics={showDemographics} />
  );
};

const DismissedBox = ({ caseId, civilCase, demographicCategories, showDemographics }) => {
  const classes = useStyles();
  // Search + demographics are shared with the jury box and pool;
  // the dismissal-reason filter is specific to this area.
  const {
    search,
    setSearch,
    demographics: demographicFilter,
    setDemographics: setDemographicFilter,
    scoreFilter,
    setScoreFilter,
  } = useSharedJurorFilters();
  const [reasonFilter, setReasonFilter] = useState([]);
  const mode = useSelector(getMode);

  const { dismissedJurors: allDismissed } = useDismissedJurors(caseId, mode);
  const { caseData } = useCase(caseId);
  const scoreDefinitions = caseData?.scoreDefinitions;
  const scoringRules = caseData?.scoringRules;
  const minimumJurorCardWidth = useViewBasedCardWidth();

  // Match the jury box card width: the box divides its row width by the
  // case's configured jurorsPerRow, with the same view-based minimum.
  // A fixed width (not just minWidth) keeps every dismissed card uniform
  // regardless of its content, so rapid-fire restores hit a predictable
  // target. Scroll activates beyond one row's worth of cards.
  const jurorsPerRow = parseInt(caseData?.jurorsPerRow || 6, 10);
  const cardWidth = `calc((100% - ${(jurorsPerRow - 1) * 8}px) / ${jurorsPerRow})`;

  const searchTerm = useDebouncedValue(search.trim(), 150);

  // "Both" is a distinct reason; only offer its chip when it's in use
  // (or already selected, so the active chip can't vanish mid-filter).
  const showBothChip =
    reasonFilter.includes('both') ||
    allDismissed.some(j => j.discardedReason === 'peremptory' && j.discarder === 'both');

  const dismissedJurors = useMemo(() => {
    return allDismissed.filter(j =>
      jurorMatchesText(j, searchTerm) &&
      jurorMatchesReasons(j, reasonFilter) &&
      jurorMatchesDemographics(j, demographicFilter) &&
      jurorMatchesScoreFilter(caseData, j, scoreFilter)
    );
  }, [allDismissed, searchTerm, reasonFilter, demographicFilter, scoreFilter, caseData]);

  return (
    <Paper
      key="dismissed-box"
      elevation={0}
      className={classNames(classes.container, classes.dismissedContainer)}
    >
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        <Typography className={classes.boxTitle} variant="h6" style={{ paddingBottom: 0 }}>
          <b>Dismissed{mode === "alternates" ? " Alternates" : ""}</b>
        </Typography>
        {allDismissed.length > 0 && (
          <FilterBar style={{ flexWrap: 'nowrap' }}>
            <FilterSearchField
              value={search}
              onChange={setSearch}
              placeholder="Search dismissed…"
              width={180}
            />
            <DemographicsFilterSelect
              jurors={allDismissed}
              value={demographicFilter}
              onChange={setDemographicFilter}
              placeholder="Demographics"
              minWidth={170}
            />
            <ScoreFilterControl
              scoreDefinitions={scoreDefinitions || []}
              value={scoreFilter}
              onChange={setScoreFilter}
            />
            <DismissalReasonChips
              value={reasonFilter}
              onChange={setReasonFilter}
              civilCase={civilCase}
              showBoth={showBothChip}
            />
          </FilterBar>
        )}
      </div>

      {allDismissed.length === 0 ? (
        <Typography variant="body2" align="center" className={classes.dismissedPreLine}>
          <b>{'\n\nDrag a juror card here to dismiss'}</b>
        </Typography>
      ) : dismissedJurors.length === 0 ? (
        <Typography variant="body2" align="center" color="textSecondary" className={classes.dismissedPreLine}>
          {'\nNo dismissed jurors match the current filters'}
        </Typography>
      ) : (
        <div style={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: 8,
          paddingBottom: 4,
        }}>
          {dismissedJurors.map(juror => (
            <div key={juror._id} style={{ flex: '0 0 auto', width: cardWidth, minWidth: minimumJurorCardWidth }}>
              <JurorCard
                type="dismissed"
                juror={juror}
                caseId={caseId}
                civilCase={civilCase}
                mode={mode}
                demographicCategories={demographicCategories}
                showDemographics={showDemographics}
                scoreDefinitions={scoreDefinitions}
                scoringRules={scoringRules}
                updateJurorInParent={updateJuror}
              />
            </div>
          ))}
        </div>
      )}
    </Paper>
  );
};

export default DismissedArea;
